import { Link, router } from '@inertiajs/react'
import { Bell, BellOff } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { PagyMeta } from '@/types/work'

// "Notifications" inbox — Sprint 5a / Unit 14c (PRODUCT-002 cut).
// Single paginated list. Click a row → PATCH marks read + redirects
// to its task. Tombstone rows (task no longer in perimeter) render
// as muted, non-clickable entries — the perimeter is authoritative,
// not the notification row.

const KIND_LABELS: Record<string, string> = {
  assigned: 'Tâche assignée',
}

interface TaskPayload {
  id: number | null
  title: string | null
  redacted: boolean
}

interface NotificationItem {
  id: number
  kind: string
  read_at: string | null
  created_at: string
  task: TaskPayload
}

interface Props {
  notifications: NotificationItem[]
  pagy: PagyMeta
  unread_count: number
}

function formatRelative(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function NotificationsIndex({ notifications, pagy, unread_count }: Props) {
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Notifications</h1>
        <p className="text-muted-foreground">
          {unread_count > 0
            ? `${unread_count} notification${unread_count > 1 ? 's' : ''} non lue${unread_count > 1 ? 's' : ''}`
            : 'Toutes les notifications sont à jour.'}
        </p>
      </div>

      {notifications.length === 0 ? (
        <EmptyState />
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y">
              {notifications.map((n) => (
                <NotificationRow key={n.id} notification={n} />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {pagy.pages > 1 && <Pagination pagy={pagy} />}
    </div>
  )
}

function NotificationRow({ notification }: { notification: NotificationItem }) {
  const isUnread = notification.read_at === null
  const isRedacted = notification.task.redacted

  function handleClick() {
    if (isRedacted) return // tombstone — task is gone for this user
    // PATCH the notification: server marks read + redirects to the task.
    // Inertia follows the redirect so the user lands on /work/tasks/:id.
    router.patch(`/work/notifications/${notification.id}`, {}, {
      preserveScroll: false,
      preserveState: false,
    })
  }

  return (
    <li
      className={cn(
        'flex items-start gap-3 px-4 py-3',
        isUnread && !isRedacted && 'bg-accent/30',
        isRedacted && 'text-muted-foreground'
      )}
    >
      <Bell
        className={cn(
          'h-4 w-4 shrink-0 mt-0.5',
          isUnread && !isRedacted ? 'text-foreground' : 'text-muted-foreground'
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge variant={isUnread && !isRedacted ? 'default' : 'outline'} className="text-xs">
            {KIND_LABELS[notification.kind] ?? notification.kind}
          </Badge>
          {isRedacted && (
            <span className="text-xs italic">Tâche hors de votre périmètre</span>
          )}
        </div>
        <p className="mt-1 text-sm">
          {isRedacted ? (
            <span className="italic">Cette tâche n'est plus accessible.</span>
          ) : (
            <button
              type="button"
              onClick={handleClick}
              className="text-left font-medium hover:underline"
            >
              {notification.task.title}
            </button>
          )}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {formatRelative(notification.created_at)}
          {notification.read_at && ' · lue'}
        </p>
      </div>
    </li>
  )
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <BellOff className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Aucune notification.</p>
      </CardContent>
    </Card>
  )
}

function Pagination({ pagy }: { pagy: PagyMeta }) {
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
  const linkFor = (page: number) => {
    if (!params) return `?page=${page}`
    params.set('page', String(page))
    return `?${params.toString()}`
  }

  // Codex P2 #89: `Button asChild disabled` wraps an anchor and the
  // `disabled` prop is silently dropped on `<a>`. Render a real button
  // for the disabled branch so out-of-range page navigations (page=0,
  // page=last+1) cannot fire from clicks at the boundaries.
  const prevDisabled = pagy.page <= 1
  const nextDisabled = pagy.page >= pagy.pages

  return (
    <div className="flex items-center justify-between text-sm">
      <p className="text-muted-foreground">
        {pagy.count} au total — page {pagy.page} sur {pagy.pages}
      </p>
      <div className="flex gap-1">
        {prevDisabled ? (
          <Button size="sm" variant="outline" disabled>
            Précédent
          </Button>
        ) : (
          <Button asChild size="sm" variant="outline">
            <Link href={linkFor(pagy.page - 1)} preserveScroll preserveState>
              Précédent
            </Link>
          </Button>
        )}
        {nextDisabled ? (
          <Button size="sm" variant="outline" disabled>
            Suivant
          </Button>
        ) : (
          <Button asChild size="sm" variant="outline">
            <Link href={linkFor(pagy.page + 1)} preserveScroll preserveState>
              Suivant
            </Link>
          </Button>
        )}
      </div>
    </div>
  )
}
